import React from 'react';
import { Settings, Truck, Map, DollarSign } from 'lucide-react';

/**
 * Компонент дополнительных параметров расчета
 */
const AdditionalOptionsSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  regions 
}) => {
  return (
    <div className="mb-6 bg-gray-50 dark:bg-gray-800/40 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Settings className="w-5 h-5 mr-2 text-gray-500" />
        Дополнительные параметры
      </h3>
      
      <div className="space-y-4">
        {/* Запас на отходы */}
        <div>
          <div className="flex items-center mb-2">
            <input
              type="checkbox"
              id="includeWastage"
              name="includeWastage"
              checked={formData.includeWastage}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <label htmlFor="includeWastage" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
              Учитывать запас на отходы
            </label>
          </div>
          
          {formData.includeWastage && (
            <div className="pl-6">
              <div className="flex items-center">
                <input
                  type="text"
                  id="wastagePercent"
                  name="wastagePercent"
                  value={formData.wastagePercent}
                  onChange={handleInputChange}
                  className={`form-input w-20 mr-2 ${errors.wastagePercent ? 'input-error' : ''}`}
                  aria-label="Процент запаса на отходы"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">% от общего объема</span>
              </div>
              {errors.wastagePercent && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.wastagePercent}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Рекомендуется 5-15% в зависимости от сложности заливки
              </p>
            </div>
          )}
        </div>

        {/* Регион и стоимость */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-gray-200 dark:border-gray-700">
          <div>
            <label htmlFor="regionPrice" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Map className="w-4 h-4 mr-1 text-gray-500" />
              Регион
            </label>
            <select
              id="regionPrice"
              name="regionPrice"
              value={formData.regionPrice}
              onChange={handleInputChange}
              className="form-select w-full"
            >
              {regions.map((region) => (
                <option key={region.id} value={region.id}>
                  {region.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="deliveryDistance" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Truck className="w-4 h-4 mr-1 text-gray-500" />
              Расстояние доставки (км)
            </label>
            <input
              type="text"
              id="deliveryDistance"
              name="deliveryDistance"
              value={formData.deliveryDistance}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.deliveryDistance ? 'input-error' : ''}`}
              disabled={!formData.includeDelivery}
            />
            {errors.deliveryDistance && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.deliveryDistance}
              </p>
            )}
          </div>
        </div>

        {/* Дополнительные услуги */}
        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Включить в расчет стоимости:
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="includeDelivery"
                checked={formData.includeDelivery}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Доставка бетона
              </span>
            </label>
            
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="includePump"
                checked={formData.includePump}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Подача бетононасосом
              </span>
            </label>
            
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="includeLabor"
                checked={formData.includeLabor}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Стоимость работ
              </span>
            </label>
          </div>
        </div>

        {/* Стоимость работ (показывается только если включена) */}
        {formData.includeLabor && (
          <div className="pl-6 pt-2">
            <label htmlFor="laborCost" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <DollarSign className="w-4 h-4 mr-1 text-gray-500" />
              Стоимость работ (руб/м³)
            </label>
            <input
              type="text"
              id="laborCost"
              name="laborCost"
              value={formData.laborCost}
              onChange={handleInputChange}
              className={`form-input w-full sm:w-1/2 ${errors.laborCost ? 'input-error' : ''}`}
            />
            {errors.laborCost && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.laborCost}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Средняя стоимость в России: 1000-2000 руб/м³ в зависимости от типа работ
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdditionalOptionsSection;